import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";import { LogOut, KeyRound, Images } from "lucide-react";
import ContentEditor from "@/components/admin/ContentEditor";
import LibraryModal from "@/components/admin/LibraryModal";
import BackToTop from "@/components/site/BackToTop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cozy_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("editor");
  const [libOpen, setLibOpen] = useState(false);

  const logout = () => { setToken(""); localStorage.removeItem(TOKEN_KEY); };

  if (!token) return <Login onToken={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;

  return (
    <div className="min-h-screen bg-cozy-bg" data-testid="admin-dashboard">
      <header className="bg-white border-b border-cozy-gold/15 shadow-cozy sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_teepee-magic-nights/artifacts/g1w0mtsx_the%20cozy%20cove%20logo.jpeg"
              alt="The Cozy Cove"
              className="w-12 h-12 rounded-full object-cover border border-cozy-gold/20 shadow-cozy"
            />
            <div>
              <div className="font-serif text-xl">The Cozy Cove</div>
              <div className="text-xs text-cozy-brown-soft">Admin Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="text-sm text-cozy-brown-soft hover:text-cozy-gold transition">View Site →</Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-cozy-beige hover:bg-cozy-pink transition">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
        <SetupReminder token={token} />
        <div className="flex gap-3 mb-8 flex-wrap">
          <Tab active={tab === "editor"} onClick={() => setTab("editor")} testid="admin-tab-editor">✨ Edit Site</Tab>
          <Tab active={false} onClick={() => setLibOpen(true)} testid="admin-tab-library">
            <Images className="inline w-3.5 h-3.5 mr-1" /> Image Library
          </Tab>
          <Tab active={tab === "account"} onClick={() => setTab("account")} testid="admin-tab-account">
            <KeyRound className="inline w-3.5 h-3.5 mr-1" /> Account
          </Tab>
        </div>

        {tab === "editor" && <ContentEditor token={token} />}
        {tab === "account" && <AccountSection token={token} onLogout={logout} />}
      </div>
      <LibraryModal open={libOpen} onClose={() => setLibOpen(false)} onPick={() => {}} token={token} manage={true} />
      <BackToTop />
    </div>
  );
}

function AccountSection({ token, onLogout }) {
  const [form, setForm] = useState({ current_password: "", new_email: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    if (!form.new_email && !form.new_password) {
      toast.error("Enter a new email or new password to update");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/credentials`,
        {
          current_password: form.current_password,
          new_email: form.new_email || undefined,
          new_password: form.new_password || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Credentials updated! Please log in again.");
      setTimeout(() => onLogout(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const cls = "mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-3 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition";

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl p-8 lg:p-10 shadow-cozy border border-cozy-gold/15 max-w-2xl space-y-5" data-testid="account-form">
      <div>
        <h2 className="font-serif text-2xl">Change Admin <span className="font-script text-cozy-gold">Credentials</span></h2>
        <p className="text-sm text-cozy-brown-soft mt-2">Update your login email and/or password. You'll need to log in again after saving.</p>
      </div>
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Current Password (required)</span>
        <input type="password" required value={form.current_password} onChange={(e) => upd("current_password", e.target.value)} className={cls} data-testid="account-current-password" />
      </label>
      <div className="border-t border-cozy-gold/15 pt-5 space-y-5">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">New Email (optional — leave blank to keep current)</span>
          <input type="email" value={form.new_email} onChange={(e) => upd("new_email", e.target.value)} className={cls} placeholder="newadmin@example.com" data-testid="account-new-email" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">New Password (optional — leave blank to keep current)</span>
          <input type="password" value={form.new_password} onChange={(e) => upd("new_password", e.target.value)} className={cls} data-testid="account-new-password" />
        </label>
        {form.new_password && (
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Confirm New Password</span>
            <input type="password" value={form.confirm_password} onChange={(e) => upd("confirm_password", e.target.value)} className={cls} data-testid="account-confirm-password" />
          </label>
        )}
      </div>
      <button disabled={saving} className="px-7 py-3 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition shadow-cozy disabled:opacity-60" data-testid="account-save">
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </form>
  );
}

function Tab({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`px-5 py-2.5 rounded-full text-sm font-medium transition ${active ? "bg-cozy-gold text-white shadow-cozy" : "bg-white text-cozy-brown hover:bg-cozy-beige"}`}>
      {children}
    </button>
  );
}

function SetupReminder({ token }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [token]);
  if (!status) return null;
  const issues = [];
  if (!status.cloudinary_configured) issues.push("Cloudinary is not configured — uploaded images may disappear after redeploys. Add CLOUDINARY_* env vars and redeploy.");
  if (!status.google_analytics_set) issues.push("Google Analytics is not set. Add your GA4 ID in Edit Site → Analytics to start tracking visitors.");
  if (!status.og_image_set) issues.push("Social Share Image is not set. Add one in Edit Site → Social Sharing to make Facebook/iMessage link previews look great.");
  if (issues.length === 0) return null;
  return (
    <div className="mb-6 rounded-3xl bg-cozy-pink/40 border border-cozy-gold/30 p-5" data-testid="setup-reminder">
      <h3 className="font-serif text-lg text-cozy-brown">A few setup tips ✨</h3>
      <ul className="mt-2 space-y-1.5 text-sm text-cozy-brown-soft">
        {issues.map((m, i) => <li key={i}>• {m}</li>)}
      </ul>
    </div>
  );
}

function Login({ onToken }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      onToken(data.token);
      toast.success("Welcome back!");
    } catch { toast.error("Invalid credentials"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cozy-bg flex items-center justify-center p-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="bg-white rounded-3xl p-10 shadow-cozy-lg w-full max-w-md space-y-5 border border-cozy-gold/15" data-testid="admin-login-form">
        <div className="text-center mb-2">
          <img
            src="https://customer-assets.emergentagent.com/job_teepee-magic-nights/artifacts/g1w0mtsx_the%20cozy%20cove%20logo.jpeg"
            alt="The Cozy Cove"
            className="w-20 h-20 rounded-full object-cover border border-cozy-gold/20 shadow-cozy mx-auto mb-4"
          />
          <h1 className="font-serif text-3xl">Admin <span className="font-script text-cozy-gold">login</span></h1>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Email</span>
          <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-3 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-cozy-brown-soft font-medium">Password</span>
          <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 w-full rounded-2xl border border-cozy-gold/25 bg-cozy-bg px-4 py-3 focus:outline-none focus:border-cozy-gold focus:ring-2 focus:ring-cozy-pink transition" />
        </label>
        <button data-testid="admin-login-submit" disabled={loading} className="w-full px-7 py-3 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition shadow-cozy disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}
